import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { api, getUser } from "../lib/api";
import { hold, release } from "@/lib/idleSession";
import { describeMediaError, describeVoiceError } from "../lib/voiceErrors";

export type DeviceType = "browser" | "phone" | "sip";
export type CallStatus = "idle" | "connecting" | "ringing" | "connected";
export type VoiceCallMode = "minimized" | "expanded";

export interface VoiceCallContextValue {
  deviceType: DeviceType | null;
  isReady: boolean;
  isInitializing: boolean;
  isOnCall: boolean;
  isOpen: boolean;
  callStatus: CallStatus;
  callDuration: number;
  isMuted: boolean;
  incomingCall: unknown | null;
  errorMessage: string | null;
  mode: VoiceCallMode;
  activePatientId: string | null;
  activePatientName: string | null;
  activePatientPhone: string | null;
  initialize: () => Promise<boolean>;
  openCallPanel: (patientId: string, patientName?: string, patientPhone?: string) => void;
  /**
   * `callerNumber` is the practice line the patient should see (10 digits).
   * Left out, the server shows the number it always has.
   */
  makeCall: (
    toNumber: string,
    patientId: string,
    patientName?: string,
    patientPhone?: string,
    callerNumber?: string,
  ) => Promise<void>;
  closeCallPanel: () => void;
  hangUp: () => void;
  toggleMute: () => void;
  acceptIncoming: () => void;
  rejectIncoming: () => void;
  minimize: () => void;
  expand: () => void;
}

interface VoiceSettings {
  deviceType: DeviceType;
  phoneNumber: string | null;
  sipAddress: string | null;
}

interface TokenResponse {
  token: string;
  identity: string;
}

const VoiceCallContext = createContext<VoiceCallContextValue | null>(null);

export function useVoiceCallContext(): VoiceCallContextValue {
  const ctx = useContext(VoiceCallContext);
  if (!ctx) {
    throw new Error("useVoiceCallContext must be used within a VoiceCallProvider");
  }
  return ctx;
}

export function VoiceCallProvider({ children }: { children: React.ReactNode }) {
  const [deviceType, setDeviceType] = useState<DeviceType | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [callStatus, setCallStatus] = useState<CallStatus>("idle");
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [incomingCall, setIncomingCall] = useState<unknown | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [mode, setMode] = useState<VoiceCallMode>("expanded");
  const [isOpen, setIsOpen] = useState(false);

  // Hold the idle-logout timer open while a call is up. A voice call produces no
  // pointer or key events, so without this a staff member on a long call is
  // signed out mid-conversation. Memory-only and capped — see lib/idleSession.ts.
  useEffect(() => {
    if (callStatus === "idle") return;
    hold("voice-call");
    return () => release("voice-call");
  }, [callStatus]);
  const [activePatientId, setActivePatientId] = useState<string | null>(null);
  const [activePatientName, setActivePatientName] = useState<string | null>(null);
  const [activePatientPhone, setActivePatientPhone] = useState<string | null>(null);

  const deviceRef = useRef<unknown>(null);
  const activeCallRef = useRef<unknown>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const callStartRef = useRef<number>(0);
  const initPromiseRef = useRef<Promise<boolean> | null>(null);

  const startTimer = useCallback(() => {
    callStartRef.current = Date.now();
    setCallDuration(0);
    timerRef.current = setInterval(() => {
      setCallDuration(Math.floor((Date.now() - callStartRef.current) / 1000));
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    callStartRef.current = 0;
  }, []);

  /**
   * Return to idle after a call ends.
   *
   * The active patient fields are deliberately left in place. The SDK fires
   * `disconnect` *before* `error` (call.js:899 then :901), so a failed call
   * closes this panel a tick before we learn why — and the `error` handler
   * below has to re-open it to show the reason. Clearing the patient here would
   * leave that re-opened panel with no name or number on it. `openCallPanel`
   * overwrites these on every open and `closeCallPanel` still clears them, so
   * they are only ever read while the panel is genuinely open.
   */
  const resetCallState = useCallback(() => {
    setCallStatus("idle");
    setCallDuration(0);
    setIsMuted(false);
    activeCallRef.current = null;
    setIsOpen(false);
    stopTimer();
  }, [stopTimer]);

  const openCallPanel = useCallback((patientId: string, patientName?: string, patientPhone?: string) => {
    if (callStatus !== "idle") {
      setMode("expanded");
      return;
    }
    setActivePatientId(patientId);
    setActivePatientName(patientName ?? null);
    setActivePatientPhone(patientPhone ?? null);
    setIsOpen(true);
    setMode("expanded");
  }, [callStatus]);

  const closeCallPanel = useCallback(() => {
    if (callStatus !== "idle") return;
    setIsOpen(false);
    setActivePatientId(null);
    setActivePatientName(null);
    setActivePatientPhone(null);
    // Dismissing the panel dismisses its failure with it. Cleared here rather
    // than on open, so a standing "voice is unavailable" from `initialize()`
    // still greets the next open.
    if (isReady) setErrorMessage(null);
  }, [callStatus, isReady]);

  const initBrowserDevice = useCallback(async (): Promise<boolean> => {
    try {
      const tokenData = await api<TokenResponse>("/voice-token", { noRedirectOn401: true });

      // Call comes along for Call.Codec: codecPreferences wants that enum, and
      // the previous `"opus" as unknown as "opus"` was a double assertion that
      // changed nothing — it still produced a string literal, which is not
      // assignable to Codec. The enum's runtime values are the same two
      // strings ("opus", "pcmu"), so nothing on the wire changes.
      const { Device, Call } = await import("@twilio/voice-sdk");

      const device = new Device(tokenData.token, {
        codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
        logLevel: "warn",
      });

      device.on("registered", () => {
        setIsReady(true);
        setErrorMessage(null);
      });

      device.on("error", (err: unknown) => {
        console.error("Twilio Device error:", err);
        setErrorMessage(describeVoiceError(err));
      });

      device.on("incoming", (call: unknown) => {
        setIncomingCall(call);
      });

      device.on("tokenWillExpire", async () => {
        try {
          const refreshed = await api<TokenResponse>("/voice-token", { noRedirectOn401: true });
          device.updateToken(refreshed.token);
        } catch (err) {
          console.error("Failed to refresh Twilio token:", err);
        }
      });

      await device.register();
      deviceRef.current = device;
      setIsReady(true);
      setErrorMessage(null);
      return true;
    } catch (err) {
      console.error("VoiceCallContext: Browser device init failed", err);
      setIsReady(false);
      setErrorMessage(err instanceof Error ? err.message : "Voice calling is unavailable for this account.");
      return false;
    }
  }, []);

  const initialize = useCallback(async (): Promise<boolean> => {
    if (isReady) return true;

    if (initPromiseRef.current) return initPromiseRef.current;

    const initPromise = (async () => {
      setIsInitializing(true);
      setErrorMessage(null);

      try {
        const settings = await api<VoiceSettings>("/voice-settings", { noRedirectOn401: true });
        setDeviceType(settings.deviceType);

        if (settings.deviceType === "browser") {
          return await initBrowserDevice();
        }

        setIsReady(true);
        return true;
      } catch (err) {
        console.error("VoiceCallContext: Failed to load settings", err);
        setIsReady(false);
        setDeviceType(null);
        setErrorMessage(err instanceof Error ? err.message : "Voice calling is unavailable for this account.");
        return false;
      } finally {
        setIsInitializing(false);
        initPromiseRef.current = null;
      }
    })();

    initPromiseRef.current = initPromise;
    return initPromise;
  }, [initBrowserDevice, isReady]);

  useEffect(() => {
    void initialize();
  }, [initialize]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      const dev = deviceRef.current as { destroy?: () => void } | null;
      if (dev?.destroy) dev.destroy();
      deviceRef.current = null;
    };
  }, []);

  const makeCall = useCallback(
    async (toNumber: string, patientId: string, patientName?: string, patientPhone?: string, callerNumber?: string) => {
      if (callStatus !== "idle") return;

      // Clear any message left over from a previous attempt, so a retry that
      // succeeds does not sit under a stale "microphone is blocked".
      setErrorMessage(null);
      setActivePatientId(patientId);
      setActivePatientName(patientName ?? null);
      setActivePatientPhone(patientPhone ?? toNumber);
      setIsOpen(true);
      setMode("expanded");

      const user = getUser();
      const accountID = user?.accountID?.toString() ?? "";

      if (deviceType === "browser") {
        const dev = deviceRef.current as {
          connect?: (opts: { params: Record<string, string> }) => Promise<unknown>;
        } | null;

        if (!dev?.connect) {
          console.error("Twilio Device not ready");
          setErrorMessage("Voice calling is still starting up. Please try again in a moment.");
          return;
        }

        // Check the microphone before dialing. `Device.connect()` fires
        // `Call.accept()` without awaiting it (device.js:581), so a mic failure
        // surfaces long after connect() resolved — as a bare `error` event on a
        // call that Twilio has already torn down. Asking here turns the most
        // common failure into an actionable message and avoids placing a call
        // that was never going to carry audio.
        try {
          const probe = await navigator.mediaDevices.getUserMedia({ audio: true });
          probe.getTracks().forEach((track) => track.stop());
        } catch (err) {
          console.error("Microphone unavailable:", err);
          setErrorMessage(describeMediaError(err));
          return;
        }

        setCallStatus("connecting");

        try {
          const call = await dev.connect({
            params: {
              To: toNumber,
              patientID: patientId,
              accountID,
              ...(callerNumber ? { callerNumber } : {}),
            },
          });

          activeCallRef.current = call;

          const typedCall = call as {
            on: (event: string, handler: (arg?: unknown) => void) => void;
            mute: (muted?: boolean) => void;
            disconnect: () => void;
          };

          // A failed call must not close this panel — that silent close is the
          // whole bug. But the SDK emits `error` and `disconnect` in *either*
          // order: a media/mic failure disconnects then errors (call.js:899,
          // :901), while a HANGUP from the gateway errors then disconnects
          // (call.js:321, :325). So the two handlers agree through this flag
          // rather than relying on which one arrives first.
          let callFailed = false;

          const endCall = () => {
            if (!callFailed) {
              resetCallState();
              return;
            }
            // Return to idle but stay on screen, so the message below explains
            // why the call never connected.
            setCallStatus("idle");
            setCallDuration(0);
            setIsMuted(false);
            activeCallRef.current = null;
            stopTimer();
            setIsOpen(true);
            setMode("expanded");
          };

          typedCall.on("ringing", () => setCallStatus("ringing"));
          typedCall.on("accept", () => {
            setCallStatus("connected");
            startTimer();
          });
          typedCall.on("disconnect", endCall);
          typedCall.on("cancel", endCall);
          typedCall.on("reject", endCall);

          // Nothing listened for this before. Every failure path in the SDK —
          // denied mic, dead audio device, ICE/media never established, a
          // gateway HANGUP — ends here, and the SDK logs it only at `debug`,
          // which our `logLevel: "warn"` drops. That is why these calls failed
          // with a clean console.
          typedCall.on("error", (err?: unknown) => {
            console.error("Twilio call error:", err);
            callFailed = true;
            setErrorMessage(describeVoiceError(err));
            endCall();
          });
        } catch (err) {
          console.error("Failed to connect call:", err);
          setErrorMessage(describeVoiceError(err));
          setCallStatus("idle");
          stopTimer();
          activeCallRef.current = null;
        }
      } else {
        setCallStatus("connecting");
        try {
          await api("/voice-call", {
            method: "POST",
            body: { toNumber, patientID: patientId, ...(callerNumber ? { callerNumber } : {}) },
          });
          setCallStatus("connected");
          startTimer();
        } catch (err) {
          // The backend's own message is the useful one here — it names the
          // missing piece ("Phone number not configured for this user").
          console.error("Failed to initiate bridge call:", err);
          setErrorMessage(describeVoiceError(err));
          setCallStatus("idle");
          stopTimer();
        }
      }
    },
    [deviceType, callStatus, startTimer, stopTimer, resetCallState],
  );

  const hangUp = useCallback(() => {
    const call = activeCallRef.current as { disconnect?: () => void } | null;
    if (call?.disconnect) {
      call.disconnect();
    }
    resetCallState();
  }, [resetCallState]);

  const toggleMute = useCallback(() => {
    if (deviceType !== "browser") return;
    const call = activeCallRef.current as {
      mute?: (muted: boolean) => void;
      isMuted?: () => boolean;
    } | null;
    if (call?.mute && call?.isMuted) {
      const newMuted = !call.isMuted();
      call.mute(newMuted);
      setIsMuted(newMuted);
    }
  }, [deviceType]);

  const acceptIncoming = useCallback(() => {
    const call = incomingCall as {
      accept?: () => void;
      on?: (event: string, handler: () => void) => void;
    } | null;
    if (!call?.accept) return;

    call.accept();
    activeCallRef.current = call;
    setIncomingCall(null);
    setCallStatus("connected");
    startTimer();

    if (call.on) {
      call.on("disconnect", () => resetCallState());
      call.on("cancel", () => resetCallState());
    }
  }, [incomingCall, startTimer, resetCallState]);

  const rejectIncoming = useCallback(() => {
    const call = incomingCall as { reject?: () => void } | null;
    if (call?.reject) call.reject();
    setIncomingCall(null);
  }, [incomingCall]);

  const minimize = useCallback(() => setMode("minimized"), []);
  const expand = useCallback(() => setMode("expanded"), []);

  const value: VoiceCallContextValue = {
    deviceType,
    isReady,
    isInitializing,
    isOnCall: callStatus !== "idle",
    isOpen,
    callStatus,
    callDuration,
    isMuted,
    incomingCall,
    errorMessage,
    mode,
    activePatientId,
    activePatientName,
    activePatientPhone,
    initialize,
    openCallPanel,
    makeCall,
    closeCallPanel,
    hangUp,
    toggleMute,
    acceptIncoming,
    rejectIncoming,
    minimize,
    expand,
  };

  return (
    <VoiceCallContext.Provider value={value}>
      {children}
    </VoiceCallContext.Provider>
  );
}
